import { ellipsis } from 'Components/DashboardTile/TileContentMaker/components/common/commonStyles';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import { fm } from 'Components/FormatMessage';
import { getSPMSLorscheiderDate, getSPMSModifiedExpandDate } from 'neuro-calculation-commons';
import { seizureType } from 'neuro-schemas';
import { exists, isNumber, isPartialDate, sortPartialDate } from 'neuro-utils';
import { append } from 'ramda';
import * as React from 'react';
import { formatPartialDate } from 'Utility/date';
import { filterDocumentsByType, sortDocumentsAscending, sortDocumentsDescending } from 'Utility/documentHandling';
import { isEmpty, uniq } from 'Utility/ramdaReplacement';
import { capitalize } from 'Utility/string';
import { isString } from 'Utility/typeGuards';
import {
  dmdDiagnoses,
  epilepsyDiagnoses,
  huntingtonDiagnoses,
  keoDiagnoses,
  reoDiagnoses,
  msDiagnoses,
  nmosdDiagnoses,
  parkinsonDiagnoses,
  sleepApneaDiagnoses,
  smaDiagnoses,
  mgravisDiagnoses,
  polyneuroDiagnoses,
} from './definitions';

import type { IDiagnosisPairs } from './definitions';

export const optionFormatter =
  (opt: number | string) =>
  (name: number | string): React.JSX.Element =>
    fm(`diagnosis.${opt}.${name}`);

export const generalOptionFormatter = (s?: string | number | boolean): React.JSX.Element =>
  fm(`general.${s ?? 'empty'}`);

export const diagnosisOptionsFormatter = (opt: string | number): React.JSX.Element => fm(`diagnosis.opts.${opt}`);

/** Get diagnoses that belong to given platforms. */
export const getDiagnosesForPlatforms = (platforms: Array<string>): Array<TDiagnosis> => {
  return uniq([
    ...(platforms.includes('sma') ? smaDiagnoses : []),
    ...(platforms.includes('dmd') ? dmdDiagnoses : []),
    ...(platforms.includes('parkinson') ? parkinsonDiagnoses : []),
    ...(platforms.includes('ms') ? [...msDiagnoses, ...keoDiagnoses, ...reoDiagnoses, ...nmosdDiagnoses] : []),
    ...(platforms.includes('huntington') ? huntingtonDiagnoses : []),
    ...(platforms.includes('epilepsy') ? epilepsyDiagnoses : []),
    ...(platforms.includes('sleepApnea') ? sleepApneaDiagnoses : []),
    ...(platforms.includes('mgravis') ? mgravisDiagnoses : []),
    ...(platforms.includes('polyneuro') ? polyneuroDiagnoses : []),
    'unspecifiedDiagnosis',
  ]);
};

export const chooseDocumentHeader = (documents: Array<IDocumentV2>, edit?: string): string => {
  let header = 'title';
  documents.map(d => {
    if (d._id === edit) {
      switch (d._type) {
        case 'parkinsonSymptoms':
          header = 'parkinsonSymptomOnset';
          break;
        case 'msSymptoms':
          header = 'msEarlySymption';
          break;
        case 'msDiagnosticCriteria':
          header = 'msDiagnosticCriteria.criteria.mainTitle.ms';
          break;
        case 'nmosdDiagnosticCriteria':
          header = 'nmosdDiagnosticCriteria.title';
          break;
        case 'pd':
          header = 'pd.header';
          break;
        case 'msa':
          header = `${
            (d._commitsLength === 1 && d._editing) || ('version' in d && d.version === '2022') ? 'msa2022' : 'msa'
          }.header`;
          break;
        case 'psp':
          header = 'psp.header';
          break;
        case 'dlb':
          header = 'dlb.header';
          break;
        case 'cbd':
          header = 'cbd.header';
          break;
        case 'ftd':
          header = 'ftd.header';
          break;
        case 'ppa':
          header = 'ppa.header';
          break;
        case 'alzheimer':
          header = 'alzheimer.header';
          break;
        case 'huntingtonSymptoms':
          header = 'huntingtonSymptomOnset';
          break;
        case 'htt':
          header = 'huntington.htt.title';
          break;
        case 'uhdrsConfidenceIndex':
          header = 'huntington.uhdrs.title';
          break;
        case 'seizureType':
          if (edit) {
            header = 'epilepsy.seizureType.titleSingle';
          } else {
            header = 'epilepsy.seizureType.title';
          }
          break;
        case 'syndrome':
          header = 'epilepsy.syndrome.title';
          break;
        case 'etiology':
          header = 'epilepsy.etiology.title';
          break;
        case 'sleepApneaFirstVisit':
          header = 'sleepApnea.sleepApneaFirstVisit.title';
          break;
        case 'respiratoryFirstVisit':
          header = 'sleepApnea.respiratoryFirstVisit.title';
          break;
        case 'sleepApneaSeverity':
          header = 'sleepApnea.sleepApneaSeverity.title';
          break;
        case 'performanceRating':
          header = 'sleepApnea.performanceRating.title';
          break;
        case 'ninmtTreatmentPeriod':
          header = 'basicTreatmentInformation';
          break;
        case 'indicationForTreatment':
          header = 'ninmt.indicationForTreatment.title';
          break;
        case 'contraIndicationsToTreatment':
          header = 'ninmt.contraIndicationsToTreatment.title';
          break;
        case 'symptomsAndLocations':
          header = 'ninmt.symptomsAndLocations.title';
          break;
        case 'referringPhysicianAndUnit':
          header = 'ninmt.referringPhysicianAndUnit.title';
          break;
        case 'symptomsDetails':
          header = 'ninmt.symptomsDetails.title';
          break;
        case 'mgfa':
          header = 'mgravis.mgfa.title';
          break;
        case 'polyneuroSymptoms':
          header = 'polyneuro.symptoms.title';
          break;
        case 'polyneuroEtiology':
          header = 'polyneuro.etiology.title';
          break;
        default:
          header = 'title';
          break;
      }
    }
  });
  return header;
};

export const arrayOfDiagnoses = (documents: Array<IDocumentWithTypeV2<'diagnosis'>>): Array<TDiagnosis> => {
  const diagnoses: Array<TDiagnosis> = [];
  for (const doc of documents) {
    if (doc.diagnosis) diagnoses.push(doc.diagnosis);
  }
  return diagnoses;
};

const hasDiagnosis = (
  n: unknown,
): n is SafeOmit<IDocumentWithTypeV2<'diagnosis'>, 'diagnosis'> &
  Required<Pick<IDocumentWithTypeV2<'diagnosis'>, 'diagnosis'>> =>
  typeof n === 'object' && n !== null && 'diagnosis' in n && isString(n.diagnosis);

/**
 * Get all diagnoses allowed to add by session platforms, capabilities and whether they already exist.
 * @param args.docs All diagnosis documents of a patient.
 * @param args.platforms List of platforms in current session.
 * @param args.capabilityGroups Capability groups in current session.
 * @returns List of diagnoses allowed to add for current session.
 */
export const getAddableDiagnoses = (args: {
  docs: IDocumentWithTypeV2<'diagnosis'>[];
  platforms: Platform[];
}): TDiagnosis[] => {
  const { docs, platforms } = args;
  // existing diagnoses are not allowed
  const existingDiagnoses = docs
    .filter(d => (d.diagnosis === 'unspecifiedDiagnosis' ? false : hasDiagnosis(d) && d._editing === false))
    .map(d => d.diagnosis);

  /** All diagnoses relevant to current session's platforms. */
  const dxsOfPlatforms = getDiagnosesForPlatforms(platforms);
  // filter out existing diagnoses
  const result = dxsOfPlatforms.filter(dx => {
    return !existingDiagnoses.includes(dx);
  });

  // prune duplicates
  return uniq<TDiagnosis>(result);
};

export const newButton = (
  type: string,
  onClick: ((e: React.MouseEvent<Element>) => void) | undefined,
  text: string,
  width?: number,
  alt?: true,
  disabled?: boolean,
  disabledTooltip?: string,
  newStyle?: boolean,
  document?: IControlProps,
): React.JSX.Element => {
  // if (text === 'general.edit' && !document) {
  //   throw new Error('Document required');
  // }
  return (
    <DocumentCreationButton
      name={type}
      document={document}
      text={text}
      newStyle={newStyle}
      onClick={onClick}
      width={width ?? 15}
      height={3}
      fontSize={16}
      alternate={alt}
      disabled={disabled}
      disabledTooltip={fm(disabledTooltip ?? 'general.documentLocked')}
      uppercase={!newStyle}
    />
  );
};

export const setDiagnosis = (
  fmString: (s: string) => string,
  diagnosis: IDiagnosis,
  courses?: Array<IMSType | undefined> | null,
): React.JSX.Element | string => {
  const thisDiagnosis = diagnosis.diagnosis ?? 'UNS';
  if (thisDiagnosis === 'G35') {
    return (
      <React.Fragment>
        {diagnosis.diagnosis}
        {courses && courses.length > 0 && courses[0]?.type ? ' (' : ''}
        {courses &&
        courses.length > 0 &&
        courses[0]?.type &&
        (courses[0].type === 'cantDefine' || courses[0].type === 'unknown')
          ? 'NA'
          : courses && courses.length > 0 && courses[0]?.type
            ? fmString(
                `diagnosis.msClinicalCourse.opts.${
                  [...courses].sort((c1, c2) => sortPartialDate(c2?.date, c1?.date))[0]?.type
                }`,
              )
            : ''}
        {courses && courses.length > 0 && courses[0]?.type ? ')' : ''}
      </React.Fragment>
    );
  } else if (thisDiagnosis.includes('unspecified')) {
    return fmString(`diagnosis.opts.${thisDiagnosis}`);
  } else if ((parkinsonDiagnoses as string[]).includes(thisDiagnosis)) {
    if (thisDiagnosis === 'alzheimerSuspicion') return fmString(`diagnosis.opts.${thisDiagnosis}`);
    return <React.Fragment>{diagnosis.diagnosis}</React.Fragment>;
  } else if (thisDiagnosis === 'G70.0') {
    const formOfDisease = diagnosis.formOfDisease?.[0];
    return (
      <React.Fragment>
        {diagnosis.diagnosis}
        {formOfDisease &&
          ` (${
            formOfDisease.formOfDisease === 'generalized'
              ? fmString(`diagnosis.mgravis.formOfDisease.generalizedShort`)
              : formOfDisease.formOfDisease === 'ocular'
                ? fmString(`diagnosis.mgravis.formOfDisease.ocularShort`)
                : ''
          })`}
      </React.Fragment>
    );
  } else {
    return thisDiagnosis;
  }
};

const appendParkinsonCriteria = (documents: Array<IDocumentV2>): Array<IDiagnosisPairs> => {
  const criterias = filterDocumentsByType(documents, [
    'pd',
    'msa',
    'dlb',
    'psp',
    'cbd',
    'ftd',
    'ppa',
    'alzheimer',
  ]).sort(sortDocumentsDescending);
  const firstSymptomsDate = formatPartialDate(
    filterDocumentsByType(documents, ['parkinsonSymptoms']).sort(sortDocumentsAscending).at(0)?.symptomOnsetDate,
  );
  const parkinsonCriteriaArray = [
    {
      header: fm('diagnosis.symptomOnsetDate'),
      value: firstSymptomsDate !== '' ? firstSymptomsDate : fm('general.noData'),
    },
    {
      header: (
        <>
          {fm('diagnosis.parkinsonLatestDiagnosticCriteria')}
          {criterias[0] && (
            <>
              {` (`}
              {fm(`diagnosis.${criterias[0]._type}.shortTitle`)}
              {`)`}
            </>
          )}
        </>
      ),
      value: criterias[0] ? <>{formatPartialDate(criterias[0].date)}</> : fm('general.noData'),
    },
  ];
  return parkinsonCriteriaArray;
};

const getAlleleLengths = (httDoc: IHTT): { [key in 'allele1' | 'allele2']: number | '-' } => {
  return {
    allele1: httDoc.cagRepeatLengths?.[0]?.cagLength ?? '-',
    allele2: httDoc.cagRepeatLengths?.[1]?.cagLength ?? '-',
  };
};

const appendHuntingtonInfo = (documents: Array<IDocumentV2>): Array<IDiagnosisPairs> => {
  let huntingtonInfoArray = [
    {
      header: fm('diagnosis.symptomOnsetDate'),
      value: formatPartialDate(
        filterDocumentsByType(documents, ['huntingtonSymptoms']).sort(sortDocumentsAscending).at(0)?.symptomOnsetDate,
      ),
    } as IDiagnosisPairs,
  ];

  const httTest = filterDocumentsByType(documents, ['htt']).sort(sortDocumentsDescending).at(0);
  const uhdrsIndex = filterDocumentsByType(documents, ['uhdrsConfidenceIndex']).sort(sortDocumentsDescending).at(0);

  const lastHuntingtonDiagnosis = filterDocumentsByType(documents, ['diagnosis'])
    .filter(d => {
      const diagnosis = d.diagnosis;
      return diagnosis && (huntingtonDiagnoses as TDiagnosis[]).includes(diagnosis);
    })
    .sort(sortDocumentsDescending)
    .at(0)?.diagnosis;

  if (httTest) {
    huntingtonInfoArray = append(
      {
        header: fm('diagnosis.huntington.htt.title'),
        value: fm(`diagnosis.huntington.htt.opts.doneNotDoneUnknown.${httTest.status}`),
      } as IDiagnosisPairs,
      huntingtonInfoArray,
    );
    if (httTest.status === 'done' && httTest.cagRepeatLengths) {
      huntingtonInfoArray = append(
        {
          header: <>{fm('diagnosis.huntington.htt.cagRepeatLengths')}</>,
          value: <>{`${getAlleleLengths(httTest).allele1} / ${getAlleleLengths(httTest).allele2}`}</>,
        } as IDiagnosisPairs,
        huntingtonInfoArray,
      );
    }
  }
  if (uhdrsIndex && lastHuntingtonDiagnosis === 'Z82') {
    huntingtonInfoArray = append(
      {
        header: fm('diagnosis.huntington.uhdrs.dash.title'),
        value:
          uhdrsIndex.index && uhdrsIndex.date ? (
            <>
              <div>{formatPartialDate(uhdrsIndex.date)}</div>
              <div>{fm(`diagnosis.huntington.uhdrs.dash.opts.${uhdrsIndex.index}`)}</div>
            </>
          ) : (
            '-'
          ),
      } as IDiagnosisPairs,
      huntingtonInfoArray,
    );
  }
  return huntingtonInfoArray;
};

const appendEpilepsyInfo = (documents: Array<IDocumentV2>): Array<IDiagnosisPairs> => {
  let epilepsyInfoArray: Array<IDiagnosisPairs> = [];

  const seizureTypeDocuments = filterDocumentsByType(documents, ['seizureType']);
  const etiologyDocuments = filterDocumentsByType(documents, ['etiology']);

  const epilepsyType = seizureType.utils.getEpilepsyType(seizureTypeDocuments);
  const etiology = etiologyDocuments.sort(sortDocumentsDescending).at(0);

  const etiologySubClasses =
    etiology &&
    !['metabolism', 'unknown'].includes(etiology.class ?? '') &&
    etiology.subClasses &&
    etiology.subClasses
      .map(subC => (subC as { [key: string]: any })[`subClass${capitalize(etiology.class ?? '')}`])
      .filter(subC => exists(subC));

  const etiologySpecificClass =
    etiology && etiology.subClasses && etiology.class === 'structural'
      ? etiology.subClasses.map(subC => subC.specificClass)
      : undefined;

  if (epilepsyType) {
    epilepsyInfoArray = append(
      {
        header: fm('diagnosis.epilepsy.seizureType.epilepsyType.title'),
        value: fm(`diagnosis.epilepsy.seizureType.epilepsyType.opts.${epilepsyType}`),
      },
      epilepsyInfoArray,
    );
  }
  if (etiology && etiology.class) {
    epilepsyInfoArray = append(
      {
        header: fm('diagnosis.epilepsy.etiology.title'),
        value: (
          <>
            {fm(`diagnosis.epilepsy.etiology.opts.${etiology.class}`)}
            {etiologySubClasses && etiologySubClasses.length > 0 ? ': ' : ''}
            {etiologySubClasses &&
              etiologySubClasses.map((subC, i) =>
                etiologySubClasses.length > 1 ? (
                  <div key={subC + i}>
                    {`${i + 1}) `}
                    {subC && fm(`diagnosis.epilepsy.etiology.opts.${subC}`)}
                    {etiologySpecificClass ? ': ' : ''}
                    {etiologySpecificClass?.[i]
                      ? fm(`diagnosis.epilepsy.etiology.opts.${etiologySpecificClass[i]}`)
                      : ''}
                  </div>
                ) : (
                  <React.Fragment key={subC + i}>
                    {subC && fm(`diagnosis.epilepsy.etiology.opts.${subC}`)}
                    {etiologySpecificClass ? ': ' : ''}
                    {etiologySpecificClass?.[i]
                      ? fm(`diagnosis.epilepsy.etiology.opts.${etiologySpecificClass[i]}`)
                      : ''}
                  </React.Fragment>
                ),
              )}
          </>
        ),
      } as IDiagnosisPairs,
      epilepsyInfoArray,
    );
  }
  return epilepsyInfoArray;
};

const appendSleepApneaInfo = (documents: Array<IDocumentV2>): Array<IDiagnosisPairs> => {
  const sleepApneaSeverities = filterDocumentsByType(documents, ['sleepApneaSeverity']);
  const performanceRatings = filterDocumentsByType(documents, ['performanceRating']);
  const respiratoryFailureDG = !isEmpty(
    filterDocumentsByType(documents, ['diagnosis']).filter(
      doc =>
        'diagnosis' in doc &&
        (doc.diagnosis === 'J96.1' || doc.diagnosis === 'J96.9' || doc.diagnosis === 'respiratoryFailureSuspicion'),
    ),
  );

  let sleepApneaInfoArray = [];

  const latestSleepApneaSeverity = sleepApneaSeverities.sort(sortDocumentsDescending).at(0);
  const latestPerformanceRating = performanceRatings.sort(sortDocumentsDescending).at(0);

  if (latestPerformanceRating) {
    sleepApneaInfoArray.push({
      header: fm('diagnosis.sleepApnea.performanceRating.title'),
      value: latestPerformanceRating.rating ?? '-',
      date: latestPerformanceRating.date,
    });
  }

  if (latestSleepApneaSeverity && !respiratoryFailureDG) {
    sleepApneaInfoArray = append(
      {
        header: fm('diagnosis.sleepApnea.sleepApneaSeverity.title'),
        value: (
          <div style={{ ...ellipsis(10) } as React.CSSProperties}>
            {fm(`diagnosis.sleepApnea.sleepApneaSeverity.opts.${latestSleepApneaSeverity.severity}`)}
          </div>
        ),
        date: latestSleepApneaSeverity.date,
      },
      sleepApneaInfoArray,
    );
  }
  return sleepApneaInfoArray;
};

const appendNinmtInfo = (documents: Array<IDocumentV2>): Array<IDiagnosisPairs> => {
  const latestTreatmentPeriod = filterDocumentsByType(documents, ['ninmtTreatmentPeriod'])
    .sort(sortDocumentsDescending)
    .at(0);

  if (!latestTreatmentPeriod) return [];

  const treatmentPeriodId = latestTreatmentPeriod._id;

  const indicationForTreatment = filterDocumentsByType(documents, ['indicationForTreatment']).find(
    d => d.treatmentPeriodId === treatmentPeriodId,
  );

  const contraIndicationsToTreatment = filterDocumentsByType(documents, ['contraIndicationsToTreatment']).filter(
    d => d.treatmentPeriodId === treatmentPeriodId,
  );

  const latestContraIndications = contraIndicationsToTreatment.sort(sortDocumentsDescending).at(0);

  const allLatestContraIndications = [
    ...(latestContraIndications?.tmsAbsoluteContraIndications ?? []),
    ...(latestContraIndications?.tmsRelativeContraIndications ?? []),
    ...(latestContraIndications?.dcsAbsoluteContraIndications ?? []),
  ];

  const ninmtInfoArray: IDiagnosisPairs[] = [];

  const maxIndicationsShown = 2;

  if (indicationForTreatment?.indication?.length) {
    const totalIndications = indicationForTreatment.indication.length;
    ninmtInfoArray.push({
      header: fm('diagnosis.ninmt.indicationForTreatment.title'),
      value: (
        <React.Fragment>
          {indicationForTreatment.indication.slice(0, maxIndicationsShown).map(
            (s, i) =>
              i < maxIndicationsShown && (
                <div key={i} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {fm(`diagnosis.ninmt.indicationForTreatment.opts.${s}`)}
                </div>
              ),
          )}
          {isNumber(totalIndications) &&
            totalIndications > maxIndicationsShown &&
            fm('diagnosis.ninmt.indicationForTreatment.otherIndications', {
              amount: totalIndications - maxIndicationsShown,
            })}
        </React.Fragment>
      ),
    });
  }

  if (latestContraIndications) {
    const totalContraIndications = allLatestContraIndications.length;
    ninmtInfoArray.push({
      header: fm('diagnosis.ninmt.contraIndicationsToTreatment.title'),
      value: (
        <React.Fragment>
          <div>{formatPartialDate(latestContraIndications.date)}</div>
          {allLatestContraIndications.slice(0, maxIndicationsShown).map(
            (s, i) =>
              i < maxIndicationsShown && (
                <div key={i} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {fm(`diagnosis.ninmt.contraIndicationsToTreatment.opts.${s}`)}
                </div>
              ),
          )}
          {totalContraIndications > maxIndicationsShown
            ? fm('diagnosis.ninmt.contraIndicationsToTreatment.otherContraIndications', {
                amount: totalContraIndications - maxIndicationsShown,
              })
            : null}
        </React.Fragment>
      ),
    } as IDiagnosisPairs);
  }
  return ninmtInfoArray;
};

const setValue = (platform: string, diagnosis?: IDiagnosis): React.JSX.Element | string | number => {
  switch (platform) {
    case 'dmd':
      return ![undefined, null, 'c.'].includes(diagnosis?.dmdMutationName) ? (diagnosis?.dmdMutationName ?? '') : '';
    default:
      return '';
  }
};

export const setCourses = (
  documents: Array<IDocumentV2>,
  platform: string,
): Array<IMSType | undefined> | undefined | null =>
  platform === 'ms' && documents.length > 0
    ? filterDocumentsByType(documents, ['diagnosis']).find(d => d.diagnosis === 'G35')?.typeOfDisease
    : undefined;

const renderMsInfo = (
  diagnosisDocs: IDocumentWithTypeV2<'diagnosis'>[],
  edssDocs: IDocumentWithTypeV2<'neurologicalStatusAndEDSS'>[],
  // TODO: Replace with IDocumentWithTypeV2<'relapse'>[] after migration is done
  relapseDocs: (IRelapse & IControlProps)[],
): boolean => {
  return getSPMSLorscheiderDate(diagnosisDocs, edssDocs, relapseDocs) ||
    getSPMSModifiedExpandDate(diagnosisDocs, edssDocs, relapseDocs)
    ? true
    : false;
};

export const setDiagnosisInfo = (
  platform: string,
  documents: Array<IDocumentV2>,
  diagnosis?: IDiagnosis,
): Array<IDiagnosisPairs> => {
  const diagnosisDocs = filterDocumentsByType(documents, ['diagnosis']);
  const edssDocs = filterDocumentsByType(documents, ['neurologicalStatusAndEDSS']);
  const relapseDocs = filterDocumentsByType(documents, ['relapse']);

  switch (platform) {
    case 'dmd':
      return [
        {
          header: fm('diagnosis.dmdMutationNameShort'),
          value: setValue(platform, diagnosis),
        },
      ];
    case 'parkinson':
      return appendParkinsonCriteria(documents);
    case 'ms': {
      const secondDiagnosis = documents.filter((d): d is IDocumentWithTypeV2<'diagnosis'> => {
        const dg = 'diagnosis' in d && d.diagnosis ? d.diagnosis : null;
        return ['G35', 'G36.0', 'G36.9', 'G37.9'].includes(dg ?? '');
      })[1];

      return renderMsInfo(diagnosisDocs, edssDocs, relapseDocs)
        ? [
            // Check if there are more MS diagnosis than one
            secondDiagnosis
              ? // Then render another row to show the second latest diagnosis
                {
                  header: secondDiagnosis.diagnosis ?? '',
                  value: isPartialDate(secondDiagnosis.date) ? formatPartialDate(secondDiagnosis.date) : '',
                }
              : { header: '', value: '' },
            // Again check if there are more than one MS diagnosis
            secondDiagnosis
              ? // Then don't render early symptoms starting date due to space issues (OK according to requirements)
                {
                  header: '',
                  value: '',
                }
              : documents.find(d => d._type === 'msSymptoms')
                ? {
                    header: fm('diagnosis.symptomOnsetDate'),
                    value: isPartialDate(documents.find(d => d._type === 'msSymptoms')?.symptomOnsetDate)
                      ? formatPartialDate(documents.find(d => d._type === 'msSymptoms')?.symptomOnsetDate)
                      : '',
                  }
                : {
                    header: '',
                    value: '',
                  },
            getSPMSModifiedExpandDate(diagnosisDocs, edssDocs, relapseDocs)
              ? {
                  header: (
                    <>
                      {fm('diagnosis.msType.possibleChangeOfMsType')}
                      {`: `}
                      {fm('diagnosis.msType.modifiedExpand')}
                    </>
                  ),
                  value: isPartialDate(getSPMSModifiedExpandDate(diagnosisDocs, edssDocs, relapseDocs))
                    ? formatPartialDate(getSPMSModifiedExpandDate(diagnosisDocs, edssDocs, relapseDocs))
                    : '',
                }
              : {
                  header: '',
                  value: '',
                },
            getSPMSLorscheiderDate(diagnosisDocs, edssDocs, relapseDocs)
              ? {
                  header: (
                    <>
                      {fm('diagnosis.msType.possibleChangeOfMsType')}
                      {`: `}
                      {fm('diagnosis.msType.lorscheider')}
                    </>
                  ),
                  value: isPartialDate(getSPMSLorscheiderDate(diagnosisDocs, edssDocs, relapseDocs))
                    ? formatPartialDate(getSPMSLorscheiderDate(diagnosisDocs, edssDocs, relapseDocs))
                    : '',
                }
              : {
                  header: '',
                  value: '',
                },
          ]
        : [
            // Check if there are more MS diagnosis than one
            secondDiagnosis
              ? {
                  header: secondDiagnosis.diagnosis ?? '',
                  value: isPartialDate(secondDiagnosis.date) ? formatPartialDate(secondDiagnosis.date) : '',
                }
              : {
                  header: '',
                  value: '',
                },
            documents.find(d => d._type === 'msSymptoms')
              ? {
                  header: fm('diagnosis.symptomOnsetDate'),
                  value: isPartialDate(documents.find(d => d._type === 'msSymptoms')?.symptomOnsetDate)
                    ? formatPartialDate(documents.find(d => d._type === 'msSymptoms')?.symptomOnsetDate)
                    : '',
                }
              : {
                  header: '',
                  value: '',
                },
          ];
    }
    case 'huntington':
      return appendHuntingtonInfo(documents);
    case 'epilepsy':
      return appendEpilepsyInfo(documents);
    case 'sleepApnea':
      return appendSleepApneaInfo(documents);
    case 'ninmt':
      return appendNinmtInfo(documents);
    case 'mgravis': {
      const symptomsDoc = documents.find(d => d._type === 'mgravisSymptoms');
      const mgfa = documents.find(d => d._type === 'mgfa');

      return [
        ...(symptomsDoc
          ? [
              {
                header: fm('diagnosis.mgravis.symptoms.date'),
                value: isPartialDate(symptomsDoc.date) ? formatPartialDate(symptomsDoc.date) : '-',
                key: 'symptomsDate',
              },
              {
                header: fm('diagnosis.mgravis.symptoms.age'),
                value: symptomsDoc.age ?? '-',
                key: 'symptomsAge',
              },
            ]
          : []),
        ...(mgfa
          ? [
              {
                header: fm('diagnosis.mgravis.mgfa.mgfaShort'),
                value: mgfa.classification ?? '-',
                key: 'mgfa',
              },
            ]
          : []),
      ];
    }
    case 'polyneuro': {
      const etiologyDoc = documents.find(d => d._type === 'polyneuroEtiology') as IPolyneuroEtiology | undefined;
      const ucDoc = documents.find(d => d._type === 'polyneuroUnderlyingCondition') as
        | IPolyneuroUnderlyingCondition
        | undefined;

      return [
        ...(etiologyDoc
          ? [
              {
                header: fm('diagnosis.polyneuro.etiology.title'),
                value: etiologyDoc.mainCategory
                  ? fm('diagnosis.polyneuro.etiology.mainCategory.' + etiologyDoc.mainCategory)
                  : '-',
                key: 'mainCategory',
              },
            ]
          : []),
        ...(etiologyDoc
          ? [
              {
                header: fm('diagnosis.polyneuro.etiology.subCategory.title'),
                value: etiologyDoc.mainCategory
                  ? fm('diagnosis.polyneuro.etiology.subCategory.' + etiologyDoc.subCategory)
                  : '-',
                key: 'subCategory',
              },
            ]
          : []),
        ...(ucDoc
          ? [
              {
                header: fm('diagnosis.polyneuro.underlyingCondition.title'),
                value: ucDoc.classification?.icd10code ?? '-',
                key: 'underlyingCondition',
              },
            ]
          : []),
      ];
    }
    default:
      return [];
  }
};

export const setBasicTreatmentInfo = (platform: string, documents: Array<IDocumentV2>): Array<IDiagnosisPairs> => {
  switch (platform) {
    case 'ninmt':
      return appendNinmtInfo(documents);
    default:
      return [];
  }
};
